import React, { useState } from 'react';

import { CardListPanel } from './components/CardListPanel';
import { DetailView } from './components/DetailView';
import { FilterBar } from './components/FilterBar';
import { useMultiSelect } from './utils/useMultiSelect';

/**
 * SplitPanelSelector - A reusable two-panel component for displaying a filterable, searchable list
 * of selectable items with a detail view. The left panel shows a list of cards that can be selected
 * via checkboxes, while the right panel displays detailed information about the clicked item.
 *
 * Features:
 * - Multi-select functionality with select all/clear all
 * - Optional filtering and search capabilities
 * - Customizable card rendering and detail views
 * - Optional confirmation modal before applying selections
 * - Responsive split-panel layout
 *
 * @param {Object} props - Component props
 *
 * @param {Array} props.items - Array of items to display in the list. Each item should be an object.
 * @param {Function} props.renderCard - Function to render each card's content.
 *   Signature: (item, state) => React.Node
 *   - item: The item object
 *   - state: { isSelected: boolean, isActive: boolean }
 * @param {Function} props.renderDetail - Function to render the detail view for the selected item.
 *   Signature: (item) => React.Node
 *   - item: The currently active item object, or null if none selected
 * @param {Function} [props.renderConfirmationModal] - Optional function to render a confirmation modal.
 *   If provided, modal will be shown before calling onApplySelected.
 *   Signature: (selectedItems, onConfirm, onCancel) => React.Node
 *   - selectedItems: Array of selected item objects
 *   - onConfirm: Function to call to confirm the action
 *   - onCancel: Function to call to cancel the action
 *
 * @param {string} [props.title='Items'] - Title text displayed in the left panel header
 * @param {string} [props.actionButtonText='Apply Selected'] - Text for the primary action button
 * @param {Function} [props.onApplySelected] - Callback function triggered when the action button is clicked
 *   (after confirmation if modal is provided).
 *   Signature: (selectedItems) => void
 *   - selectedItems: Array of selected item objects
 * @param {string} [props.itemKeyField='id'] - Field name to use as the unique identifier for items.
 *   Each item must have this field and it must be unique.
 * @param {string} [props.emptyStateMessage='No items found'] - Message displayed when the items array is empty
 *
 * @param {Object} [props.filterConfigs] - Configuration object for the filter bar.
 *   If not provided, the filter bar will not be rendered.
 *   Shape: { filters: Array<{ name, label, type, options }> }
 * @param {Object} [props.displayConfig] - Configuration object for the display menu (sorting, view options, etc.).
 *   Shape depends on FilterBar component requirements.
 * @param {Function} [props.onFilterChange] - Callback triggered when filter values change.
 *   Signature: (filterValues) => void
 *   - filterValues: Object mapping filter names to their current values
 * @param {Function} [props.onSearch] - Callback triggered when search is performed.
 *   Signature: (searchTerm) => void
 *   - searchTerm: The current search string
 *
 * @param {Array} [props.defaultSelected=[]] - Array of item IDs (using itemKeyField) that should be
 *   selected by default when the component mounts
 *
 * @example
 * <SplitPanelSelector
 *   items={products}
 *   itemKeyField="productId"
 *   title="Products"
 *   actionButtonText="Add to Cart"
 *   renderCard={(product, { isSelected, isActive }) => (
 *     <div className={isActive ? 'active' : ''}>
 *       <h3>{product.name}</h3>
 *       <p>{product.price}</p>
 *     </div>
 *   )}
 *   renderDetail={(product) => (
 *     product ? <ProductDetails product={product} /> : <p>Select a product</p>
 *   )}
 *   onApplySelected={(selectedProducts) => {
 *     console.log('Adding products:', selectedProducts);
 *   }}
 *   filterConfigs={{
 *     filters: [
 *       { name: 'category', label: 'Category', type: 'select', options: categories }
 *     ]
 *   }}
 *   onFilterChange={(filters) => console.log('Filters changed:', filters)}
 *   onSearch={(term) => console.log('Searching for:', term)}
 *   defaultSelected={[1, 2, 3]}
 * />
 */
export function SplitPanelSelector({
    actionButtonText = 'Apply Selected',
    defaultSelected = [],
    displayConfig,
    emptyStateMessage = 'No items found',
    filterConfigs,
    itemKeyField = 'id',
    items = [],
    onApplySelected,
    onFilterChange,
    onSearch,
    renderCard,
    renderConfirmationModal,
    renderDetail,
    title = 'Items',
}) {
    const [activeItemId, setActiveItemId] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const getKey = (item) => item[itemKeyField];

    const { clear, selectAll, selectedIds, selectedItems, toggle } = useMultiSelect(items, getKey, defaultSelected);

    // Find active item
    const activeItem = items.find((item) => getKey(item) === activeItemId);

    // Detail view handler
    const handleItemClick = (itemId) => {
        setActiveItemId(itemId);
    };

    // Action handlers
    const handleApplyClick = () => {
        if (renderConfirmationModal) {
            setShowConfirmationModal(true);
        } else if (onApplySelected) {
            onApplySelected(selectedItems);
        }
    };

    const handleConfirm = () => {
        setShowConfirmationModal(false);
        if (onApplySelected) {
            onApplySelected(selectedItems);
        }
    };

    const handleCancel = () => {
        setShowConfirmationModal(false);
    };

    return (
        <div className="flex flex-col h-full">
            {/* Filter Bar */}
            {filterConfigs && (
                <div className="mb-4 bg-white border-b">
                    <FilterBar
                        filterConfigs={filterConfigs}
                        displayConfig={displayConfig}
                        onFilterChange={onFilterChange}
                        onSearch={onSearch}
                    />
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex flex-1 gap-6 min-h-0">
                {/* Left Panel - Card List */}
                <div className="w-1/2 flex flex-col min-h-0">
                    <CardListPanel
                        items={items}
                        selectedIds={selectedIds}
                        activeItemId={activeItemId}
                        getKey={getKey}
                        renderCard={renderCard}
                        onToggleSelection={toggle}
                        onItemClick={handleItemClick}
                        onSelectAll={selectAll}
                        onClearSelection={clear}
                        emptyStateMessage={emptyStateMessage}
                        title={title}
                        actionButtonText={actionButtonText}
                        onApplyClick={handleApplyClick}
                    />
                </div>

                {/* Right Panel - Detail View */}
                <div className="w-1/2">
                    <DetailView activeItem={activeItem} renderDetail={renderDetail} />
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmationModal &&
                renderConfirmationModal &&
                renderConfirmationModal(selectedItems, handleConfirm, handleCancel)}
        </div>
    );
}
